import { X, FileText, User, Clock, Send, Bell, Calendar, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { QuestionnaireSend } from '@/types/questionnaire.types';
import {
  SendStatusLabels,
  SendStatusColors,
  DeliveryChannelLabels,
} from '@/types/questionnaire.types';

interface QuestionnaireSendDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  send: QuestionnaireSend | null;
  onViewResponse?: () => void;
}

export function QuestionnaireSendDetailDialog({
  open,
  onOpenChange,
  send,
  onViewResponse,
}: QuestionnaireSendDetailDialogProps) {
  if (!open || !send) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const getStatusIcon = () => {
    switch (send.status) {
      case 'ANSWERED':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'EXPIRED':
        return <Clock className="h-5 w-5 text-orange-500" />;
      case 'FAILED':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Send className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusDescription = () => {
    switch (send.status) {
      case 'PENDING':
        return 'O questionário está aguardando envio.';
      case 'SENT':
        return 'O questionário foi enviado e está aguardando entrega.';
      case 'DELIVERED':
        return 'O questionário foi entregue ao paciente e aguarda resposta.';
      case 'ANSWERED':
        return 'O paciente respondeu ao questionário.';
      case 'EXPIRED':
        return 'O prazo para resposta expirou.';
      case 'FAILED':
        return 'Houve uma falha no envio do questionário.';
      default:
        return '';
    }
  };

  const isExpired = send.expiresAt && new Date(send.expiresAt) < new Date();
  const colors = SendStatusColors[send.status];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-background p-6 shadow-lg">
        {/* Header */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Detalhes do Envio</h2>
              <p className="text-sm text-muted-foreground">{send.questionnaireName}</p>
            </div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-1 hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Status Banner */}
        <div className={`mb-4 flex items-start gap-3 rounded-lg p-3 ${colors.bg}`}>
          {getStatusIcon()}
          <div>
            <p className={`font-medium ${colors.text}`}>
              {SendStatusLabels[send.status]}
            </p>
            <p className={`text-sm ${colors.text} opacity-80`}>
              {getStatusDescription()}
            </p>
          </div>
        </div>

        {/* Details Grid */}
        <div className="space-y-4">
          {/* Paciente */}
          <div className="flex items-start gap-3">
            <User className="mt-0.5 h-4 w-4 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">Paciente</p>
              <p className="font-medium">{send.patientName}</p>
            </div>
          </div>

          {/* Linha de Cuidado */}
          {send.careLineName && (
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Linha de Cuidado</p>
                <p className="font-medium">{send.careLineName}</p>
              </div>
            </div>
          )}

          {/* Datas */}
          <div className="grid grid-cols-2 gap-4">
            {/* Criado em */}
            <div className="flex items-start gap-3">
              <Calendar className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Criado em</p>
                <p className="font-medium">{formatDate(send.createdAt)}</p>
                {send.createdByName && (
                  <p className="text-xs text-muted-foreground">por {send.createdByName}</p>
                )}
              </div>
            </div>

            {/* Enviado em */}
            {send.sentAt && (
              <div className="flex items-start gap-3">
                <Send className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Enviado em</p>
                  <p className="font-medium">{formatDate(send.sentAt)}</p>
                  {send.sentVia && (
                    <p className="text-xs text-muted-foreground">
                      via {DeliveryChannelLabels[send.sentVia]}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Agendamento */}
          {send.scheduledAt && (
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Agendado para</p>
                <p className="font-medium">{formatDate(send.scheduledAt)}</p>
              </div>
            </div>
          )}

          {/* Expiração */}
          {send.expiresAt && (
            <div className="flex items-start gap-3">
              <Clock className={`mt-0.5 h-4 w-4 ${isExpired ? 'text-red-500' : 'text-muted-foreground'}`} />
              <div>
                <p className="text-sm text-muted-foreground">
                  {isExpired ? 'Expirou em' : 'Expira em'}
                </p>
                <p className={`font-medium ${isExpired ? 'text-red-600' : ''}`}>
                  {formatDate(send.expiresAt)}
                </p>
              </div>
            </div>
          )}

          {/* Respondido em */}
          {send.answeredAt && (
            <div className="flex items-start gap-3">
              <CheckCircle className="mt-0.5 h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Respondido em</p>
                <p className="font-medium text-green-600">{formatDate(send.answeredAt)}</p>
              </div>
            </div>
          )}

          {/* Lembretes */}
          {send.reminderCount > 0 && (
            <div className="flex items-start gap-3">
              <Bell className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Lembretes enviados</p>
                <p className="font-medium">{send.reminderCount}</p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-lg border px-4 py-2 hover:bg-muted"
          >
            Fechar
          </button>
          {send.status === 'ANSWERED' && send.responseId && onViewResponse && (
            <button
              onClick={onViewResponse}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
            >
              <FileText className="h-4 w-4" />
              Ver Resposta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
